import { useEffect, useState } from "react";
import axios from "axios";
import { ArrowRight, ImageOff, Play } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function GalleryPreview({ onSeeAll, onTour }) {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/studio/photos`)
      .then((res) => {
        if (cancelled) return;
        setPhotos((res.data || []).slice(0, 8));
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) return null;

  return (
    <section
      id="galeria-preview"
      className="px-6 md:px-12 lg:px-24 py-16 md:py-24 bg-[#0a0a0a]"
      data-testid="gallery-preview-section"
    >
      <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
        <div>
          <div className="flex items-center gap-3 mb-3">
            <div className="h-px w-10 bg-yellow-400" />
            <span className="overline">O Estúdio</span>
          </div>
          <h2 className="font-display uppercase text-4xl md:text-5xl tracking-tight leading-[0.95]">
            Conhece o <span className="text-yellow-400">espaço</span>
          </h2>
          <p className="mt-4 text-neutral-400 text-sm md:text-base max-w-xl">
            Duas zonas de treino, equipamento premium e o ambiente exclusivo onde acontece a tua sessão.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {onTour ? (
            <button
              type="button"
              onClick={onTour}
              className="btn-primary text-xs inline-flex items-center gap-2"
              data-testid="gallery-preview-tour"
            >
              <Play size={14} /> Tour Virtual
            </button>
          ) : null}
          <button
            type="button"
            onClick={onSeeAll}
            className="btn-ghost text-xs inline-flex items-center gap-2"
            data-testid="gallery-preview-see-all"
          >
            Ver todas as fotos <ArrowRight size={14} />
          </button>
        </div>
      </div>

      {photos.length === 0 ? (
        <div className="border border-neutral-800 bg-neutral-950/40 p-12 text-center">
          <ImageOff size={36} className="mx-auto text-neutral-700 mb-3" />
          <div className="text-neutral-400">
            Fotografias do estúdio em breve.
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
          {photos.map((p, i) => (
            <button
              key={p.id}
              type="button"
              onClick={onSeeAll}
              className={`relative group overflow-hidden border border-neutral-900 hover:border-yellow-400/60 transition-colors bg-neutral-950 ${i === 0 ? "row-span-2 col-span-2 md:row-span-2 md:col-span-2" : ""}`}
              data-testid={`gallery-preview-item-${p.id}`}
            >
              <div className={i === 0 ? "aspect-[4/3] md:aspect-square" : "aspect-square"}>
                <img
                  src={p.foto}
                  alt={p.titulo || "Estúdio O-meu-Pt.pt"}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  loading="lazy"
                />
              </div>
              {p.titulo ? (
                <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/95 to-transparent text-left opacity-0 group-hover:opacity-100 transition-opacity">
                  <div className="text-sm font-bold uppercase tracking-wide truncate">
                    {p.titulo}
                  </div>
                </div>
              ) : null}
            </button>
          ))}
        </div>
      )}
    </section>
  );
}
